import React, { useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Link } from 'react-router-dom';
import { ArrowRight, InfoIcon } from 'lucide-react';
import { usePageSeo } from '@/hooks/usePageSeo';
import ShareMenu from '@/components/ShareMenu';

interface StatePropertyTaxPageProps {
  stateName: string;
  processDescription: string;
  noticeOfValueMailed: string;
  noticeOfValueMailedDateExplanation: string;
  valuationAsOfDate: string;
  valuationAsOfDateExplanation: string;
  deadlineExplanation: string;
  stateStatute?: string;
}

const StatePropertyTaxPage: React.FC<StatePropertyTaxPageProps> = ({
  stateName,
  processDescription,
  noticeOfValueMailed,
  noticeOfValueMailedDateExplanation,
  valuationAsOfDate,
  valuationAsOfDateExplanation,
  deadlineExplanation,
  stateStatute
}) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const cleanText = (text: string) => text.replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();

  const seoDescription = `How to appeal property taxes in ${stateName} in 2026. Learn about ${stateName} property tax deadlines, valuation processes, and step-by-step appeal procedures.`;
  const stateSlug = stateName.toLowerCase().replace(/\s+/g, '-');

  usePageSeo({
    title: `How to Appeal Property Taxes in ${stateName} — 2026 Deadlines & Guide | PropertyTaxHero`,
    description: seoDescription,
    path: `/${stateSlug}`,
    ogType: 'article',
    schema: [
      { '@context': 'https://schema.org', '@type': 'Article', headline: `How to Appeal Property Taxes in ${stateName} — 2026 Deadlines & Guide`, description: seoDescription, url: `https://propertytaxhero.com/${stateSlug}`, publisher: { '@type': 'Organization', name: 'PropertyTaxHero', url: 'https://propertytaxhero.com' }, datePublished: '2026-01-01', dateModified: '2026-03-01' },
      { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: 'Home', item: 'https://propertytaxhero.com/' }, { '@type': 'ListItem', position: 2, name: 'State Guides', item: 'https://propertytaxhero.com/all-states' }, { '@type': 'ListItem', position: 3, name: stateName, item: `https://propertytaxhero.com/${stateSlug}` }] },
    ],
  });

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-tax-backgroundLight to-white">        
        <article className="container mx-auto px-4 py-8 max-w-4xl">
          <nav aria-label="Breadcrumb" className="text-sm text-tax-neutralGray mb-6">
            <Link to="/" className="hover:text-tax-brightBlue">Home</Link> &gt;{' '}
            <Link to="/all-states" className="hover:text-tax-brightBlue">State Guides</Link> &gt;{' '}
            <span>{stateName}</span>
          </nav>

          <div className="flex justify-end mb-2">
            <ShareMenu
              path={`/${stateSlug}`}
              title={`How to Appeal Property Taxes in ${stateName} — 2026 Deadlines & Guide | PropertyTaxHero`}
              description={seoDescription}
              variant="button"
            />
          </div>

          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-tax-deepBlue mb-6">
              How to Appeal Property Taxes in {stateName} — 2026 Deadlines &amp; Guide
            </h1>
            <p className="text-xl text-tax-neutralGray max-w-3xl mx-auto leading-relaxed">
              Complete guide to appealing your property tax assessment in {stateName}. Learn about deadlines, filing procedures, and how PropertyTaxHero can help you prepare a stronger case.
            </p>
          </div>

          {/* Important Notice */}
          <Alert className="mb-8 border-tax-accentBlue bg-tax-accentBlue/5">
            <InfoIcon className="h-4 w-4" />
            <AlertDescription className="text-tax-neutralGray">
              Property taxes are generally administered at the county or township level. Please verify with your local assessor the exact procedures and due dates. The following is a general guide for the state, but your local municipality may differ. Enter your property's address on <Link to="/" className="text-tax-accentBlue font-semibold hover:text-tax-brightBlue underline">PropertyTaxHero.com</Link> to receive personalized instructions for your property's local government.
            </AlertDescription>
          </Alert>

          {/* Call to Action */}
          <div className="text-center mb-8">
            <Card className="p-8 bg-tax-backgroundLight border-tax-accentBlue/20">
              <h2 className="text-3xl font-bold mb-4 text-tax-deepBlue">
                Get Personalized Help for Your {stateName} Property
              </h2>
              <p className="text-xl mb-8 text-tax-neutralGray max-w-2xl mx-auto">
                Enter your property address to receive customized appeal guidance and deadline reminders for your specific county
              </p>
              <Link to="/">
                <Button 
                  size="lg"
                  className="bg-tax-deepBlue hover:bg-tax-brightBlue text-white font-bold text-lg px-8 py-3 rounded-xl shadow-md hover:shadow-lg transition-all"
                >
                  Start Your Free Assessment
                  <ArrowRight className="ml-3 h-5 w-5" />
                </Button>
              </Link>
            </Card>
          </div>

          {/* Content Sections */}
          <div className="space-y-8">
            {/* Appeal Procedures */}
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-tax-deepBlue mb-6">
                Procedures To Appeal Your Taxable Valuation
              </h2>
              <div className="prose prose-lg max-w-none text-tax-neutralGray">
                <p className="leading-relaxed">
                  {cleanText(processDescription)}
                </p>
              </div>
            </Card>

            {/* Notice of Value */}
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-tax-deepBlue mb-6">
                Notice of Value (NOV)
              </h2>
              <div className="prose prose-lg max-w-none text-tax-neutralGray">
                <p className="leading-relaxed mb-4">
                  You should receive a notice of value or similar document from your assessor. This should be around <strong>{noticeOfValueMailed}</strong>.
                </p>
                <p className="leading-relaxed">
                  {cleanText(noticeOfValueMailedDateExplanation)}
                </p>
              </div>
            </Card>

            {/* Property Valuation */}
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-tax-deepBlue mb-6">
                Property Valuation
              </h2>
              <div className="prose prose-lg max-w-none text-tax-neutralGray">
                <p className="leading-relaxed mb-4">
                  Properties are valued as of <strong>{valuationAsOfDate}</strong>.
                </p>
                <p className="leading-relaxed">
                  {cleanText(valuationAsOfDateExplanation)}
                </p>
              </div>
            </Card>

            {/* Deadlines */}
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-tax-deepBlue mb-6">
                Deadlines to Appeal
              </h2>
              <div className="prose prose-lg max-w-none text-tax-neutralGray">
                <p className="leading-relaxed">
                  {cleanText(deadlineExplanation)}
                </p>
              </div>
            </Card>

            {/* State Statute Link */}
            {stateStatute && (
              <Card className="p-6">
                <h2 className="text-xl font-bold text-tax-deepBlue mb-4">
                  {stateName} Property Tax Laws
                </h2>
                <p className="text-tax-neutralGray mb-4">
                  For complete legal details and current statutes governing property tax in {stateName}:
                </p>
                <a 
                  href={stateStatute} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <Button className="bg-tax-deepBlue hover:bg-tax-brightBlue text-white transition-colors">
                    View {stateName} Property Tax Statutes
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </Card>
            )}
          </div>

          {/* Bottom CTA */}
          <div className="mt-12 text-center">
            <Card className="p-8 bg-tax-backgroundLight">
              <h3 className="text-2xl font-bold text-tax-deepBlue mb-4">
                Ready to Challenge Your {stateName} Property Tax Assessment?
              </h3>
              <p className="text-lg text-tax-neutralGray mb-6">
                Visit PropertyTaxHero.com and enter your property address for personalized information and free help preparing a valuation appeal.
              </p>
              <Link to="/">
                <Button 
                  size="lg" 
                  className="bg-tax-deepBlue hover:bg-tax-brightBlue text-white transition-colors"
                >
                  Get Free Help Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </Card>
          </div>
        </article>
      </div>
    </Layout>
  );
};

export default StatePropertyTaxPage;